import React, { memo, useState } from 'react'
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  IconButton,
  Link,
  Tooltip,
  Typography,
} from '@mui/material'
import {
  AttachFile,
  Handshake,
  OpenInNew,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material'

const themeColors = {
  primary: '#232536',
}

const getRelativeTime = (date) => {
  if (!date) return ''
  let d
  if (date instanceof Date) {
    d = date
  } else {    
    const s = typeof date === 'string' && !/[Zz]|[+-]\d{2}:?\d{2}$/.test(date) ? date + 'Z' : date
    d = new Date(s)
  }
  if (isNaN(d.getTime())) return ''
  const diffMs = Date.now() - d.getTime()
  const sec = Math.floor(diffMs / 1000)
  if (sec < 60) return 'Just now'
  const min = Math.floor(sec / 60)
  if (min < 60) return `${min} minute${min !== 1 ? 's' : ''} ago`
  const hr = Math.floor(min / 60)
  if (hr < 24) return `${hr} hour${hr !== 1 ? 's' : ''} ago`
  const day = Math.floor(hr / 24)
  if (day < 7) return `${day} day${day !== 1 ? 's' : ''} ago`
  const week = Math.floor(day / 7)
  if (week < 5) return `${week} week${week !== 1 ? 's' : ''} ago`
  const month = Math.floor(day / 30)
  if (month < 12) return `${month} month${month !== 1 ? 's' : ''} ago`
  const year = Math.floor(day / 365)
  return `${year} year${year !== 1 ? 's' : ''} ago`
}

const formatDate = (dateStr) => {
  if (!dateStr) return null
  const s = typeof dateStr === 'string' && !/[Zz]|[+-]\d{2}:?\d{2}$/.test(dateStr) ? dateStr + 'Z' : dateStr
  const d = new Date(s)
  if (isNaN(d.getTime())) return dateStr
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}

const DocumentTypeChip = ({ type }) => {
  if (!type) return null
  const colorMap = {
    circular: { bg: '#ede9fe', border: '#c4b5fd', text: '#7c3aed' },
    regulation: { bg: '#dbeafe', border: '#93c5fd', text: '#1d4ed8' },
    law: { bg: '#fce7f3', border: '#f9a8d4', text: '#be185d' },
    decree: { bg: '#fef3c7', border: '#fcd34d', text: '#92400e' },
    guideline: { bg: '#d1fae5', border: '#6ee7b7', text: '#065f46' },
    notice: { bg: '#e0f2fe', border: '#7dd3fc', text: '#075985' },
  }
  const key = type.toLowerCase()
  const color = Object.keys(colorMap).find((k) => key.includes(k))
  const c = colorMap[color] ?? { bg: '#f1f5f9', border: '#cbd5e1', text: '#475569' }
  return (
    <Chip
      label={type}
      size='small'
      sx={{
        bgcolor: c.bg,
        border: `1px solid ${c.border}`,
        color: c.text,
        fontWeight: 600,
        fontSize: '0.7rem',
        height: 20,
      }}
    />
  )
}

const DocumentCard = ({
  document,
  selected = false,
  onToggleSelect,
  onMarkSeen,
}) => {
  const {
    title,
    documentType,
    referenceNumber,
    partnerCountry,
    attachments,
    pageUrl,
    publishDate,
    effectiveDate,
    scrapedAt,
    taxType,
    source,
    seenStatus,
    seenBy,
    seenAt,
  } = document

  const seen = seenStatus === 'REVIEWED'

  const [busy, setBusy] = useState(false)

  const attachmentList = Array.isArray(attachments) ? attachments : []
  const taxTypes = taxType ? (Array.isArray(taxType) ? taxType : [taxType]) : []

  const docId = document._id ?? document.id

  const handleMarkSeen = async (e) => {
    e?.stopPropagation()
    if (busy || !onMarkSeen || !docId) return
    setBusy(true)
    try {
      await onMarkSeen(docId, !seen)
    } finally {
      setBusy(false)
    }
  }

  const handleSelectClick = (e) => {
    e.stopPropagation()
    if (docId && onToggleSelect) onToggleSelect(docId)
  }

  return (
    <Card
      elevation={0}
      sx={{
        position: 'relative',
        border: '1px solid',
        borderColor: selected ? '#23252a' : seen ? '#d7dee8' : '#e2e8f0',
        borderRadius: 2,
        width: '100%',
        height: '100%',
        minHeight: 280,
        display: 'flex',
        flexDirection: 'column',
        transition: 'border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease',
        bgcolor: seen ? '#f8fafc' : '#fff',
        boxShadow: selected ? '0 0 0 2px rgba(35,37,42,0.08)' : 'none',
        '&:hover': {
          borderColor: selected ? '#23252a' : '#94a3b8',
          boxShadow: '0 10px 24px rgba(15,23,42,0.08)',
          transform: 'translateY(-1px)',
        },
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 6,
          right: 6,
          zIndex: 3,
          bgcolor: 'rgba(255,255,255,0.94)',
          borderRadius: '50%',
          boxShadow: selected ? '0 2px 8px rgba(15,23,42,0.14)' : 'none',
        }}
      >
        <Checkbox
          size='small'
          checked={selected}
          disabled={!docId}
          onChange={handleSelectClick}
          onClick={(e) => e.stopPropagation()}
          sx={{
            p: 0.5,
            color: '#94a3b8',
            '&.Mui-checked': { color: '#23252a' },
          }}
        />
      </Box>

      <CardContent
        sx={{
          p: 2,
          '&:last-child': { pb: 2 },
          display: 'flex',
          flexDirection: 'column',
          gap: 0.75,
          overflow: 'hidden',
          flex: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, pr: 3 }}>
          {!seen && (
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.4,
                px: 0.75,
                py: 0.2,
                borderRadius: '4px',
                bgcolor: '#f0fdf4',
                border: '1px solid #bbf7d0',
              }}
            >
              <Box
                sx={{
                  width: 5,
                  height: 5,
                  borderRadius: '50%',
                  bgcolor: '#16a34a',
                  flexShrink: 0,
                }}
              />
              <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#15803d', lineHeight: 1 }}>
                New
              </Typography>
            </Box>
          )}
        </Box>

        {/* Top chips row */}
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
          <DocumentTypeChip type={documentType} />
          {taxTypes.map((t) => (
            <Chip
              key={t}
              label={t}
              size='small'
              sx={{ bgcolor: '#fff7ed', border: '1px solid #fdba74', color: '#c2410c', fontSize: '0.68rem', height: 20 }}
            />
          ))}
          {partnerCountry && (
            <Chip
              icon={<Handshake sx={{ fontSize: '0.75rem !important' }} />}
              label={`Partner: ${partnerCountry}`}
              size='small'
              sx={{ bgcolor: '#f0fdf4', border: '1px solid #bbf7d0', color: '#166534', fontSize: '0.68rem', height: 20 }}
            />
          )}
          {referenceNumber && (
            <Chip
              label={`${referenceNumber}`}
              size='small'
              sx={{ bgcolor: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0', fontSize: '0.68rem', height: 20 }}
            />
          )}
        </Box>

        {/* Title */}
        <Typography
          variant='subtitle2'
          sx={{
            fontWeight: seen ? 600 : 700,
            fontSize: '0.92rem',
            color: seen ? '#475569' : themeColors.primary,
            lineHeight: 1.4,
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: 2,
            overflow: 'hidden',
          }}
        >
          {title || '(No title)'}
        </Typography>

        {/* Meta */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, pt: 0.5, borderTop: '1px solid #f1f5f9', flexWrap: 'wrap' }}>
          {source && (
            <Typography variant='caption' sx={{ color: '#475569', fontSize: '0.72rem', fontStyle: 'italic', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {source}
            </Typography>
          )}
          {publishDate ? (
            <Tooltip title={`Published: ${formatDate(publishDate)}${effectiveDate && effectiveDate !== publishDate ? ` · Effective: ${formatDate(effectiveDate)}` : ''}`}>
              <Typography variant='caption' sx={{ color: '#94a3b8', fontSize: '0.7rem', whiteSpace: 'nowrap', flexShrink: 0, cursor: 'default' }}>
                {getRelativeTime(publishDate)}
              </Typography>
            </Tooltip>
          ) : scrapedAt ? (
            <Tooltip title={`Scraped on: ${formatDate(scrapedAt)}`}>
              <Typography
                variant='caption'
                sx={{ color: '#a78bfa', fontSize: '0.7rem', whiteSpace: 'nowrap', flexShrink: 0, cursor: 'default', fontStyle: 'italic' }}
              >
                Scraped {getRelativeTime(scrapedAt)}
              </Typography>
            </Tooltip>
          ) : null}
        </Box>

        {/* Attachments */}
        {attachmentList.length > 0 && (
          <Box sx={{ pt: 0.5, borderTop: '1px solid #f1f5f9' }}>
            <Typography
              variant='caption'
              sx={{ color: '#6b7280', fontWeight: 600, mb: 0.5, display: 'flex', alignItems: 'center', gap: 0.4, fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}
            >
              <AttachFile sx={{ fontSize: 12 }} />
              Attachments
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.4,
                maxHeight: attachmentList.length > 2 ? 72 : 'none',
                overflowY: attachmentList.length > 2 ? 'auto' : 'visible',
                pr: attachmentList.length > 2 ? 0.5 : 0,
                '&::-webkit-scrollbar': { width: 4 },
                '&::-webkit-scrollbar-track': { bgcolor: '#f1f5f9', borderRadius: 2 },
                '&::-webkit-scrollbar-thumb': { bgcolor: '#cbd5e1', borderRadius: 2 },
              }}
            >
              {attachmentList.map((att, i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
                  <Link
                    href={att.url}
                    target='_blank'
                    rel='noopener noreferrer'
                    underline='hover'
                    onClick={(e) => e.stopPropagation()}
                    sx={{ fontSize: '0.76rem', color: '#1d4ed8', fontWeight: 500, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  >
                    {att.title || att.url}
                  </Link>
                  {att.size && (
                    <Typography variant='caption' sx={{ color: '#94a3b8', fontSize: '0.66rem', whiteSpace: 'nowrap', flexShrink: 0 }}>
                      {att.size}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* Footer action row */}
        {onMarkSeen && (
          <Box
            sx={{
              mt: 'auto',
              pt: 1,
              borderTop: '1px solid #f1f5f9',
              display: 'flex',
              flexDirection: 'column',
              gap: 0.75,
            }}
          >
            {seenAt && (
              <Tooltip title={`Reviewed on ${formatDate(seenAt)}${seenBy ? ` by ${seenBy}` : ''}`}>
                <Typography
                  variant='caption'
                  sx={{ color: '#64748b', fontSize: '0.7rem', fontStyle: 'italic', cursor: 'default' }}
                >
                  {getRelativeTime(seenAt)}{seenBy ? ` · ${seenBy}` : ''}
                </Typography>
              </Tooltip>
            )}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: pageUrl ? '1fr auto' : '1fr',
                gap: 1,
                alignItems: 'center',
              }}
            >
            <Button
              onClick={handleMarkSeen}
              disabled={busy || !docId}
              size='small'
              startIcon={seen ? <VisibilityOff sx={{ fontSize: 14 }} /> : <Visibility sx={{ fontSize: 14 }} />}
              sx={{
                justifyContent: 'center',
                px: 1.25,
                py: 0.55,
                fontSize: '0.74rem',
                fontWeight: 600,
                borderRadius: '6px',
                border: '1px solid',
                textTransform: 'none',
                transition: 'all 0.15s ease',
                bgcolor: '#f8fafc',
                color: '#475569',
                borderColor: '#e2e8f0',
                '&:hover': {
                  bgcolor: '#f1f5f9',
                  borderColor: '#94a3b8',
                  color: '#23252a',
                },
                '&.Mui-disabled': { opacity: 0.5 },
              }}
            >
              {seen ? 'Mark as Unread' : 'Mark as Reviewed'}
            </Button>
            {pageUrl && (
              <Tooltip title='Open source page'>
                <IconButton
                  size='small'
                  component='a'
                  href={pageUrl}
                  target='_blank'
                  rel='noopener noreferrer'
                  onClick={(e) => e.stopPropagation()}
                  sx={{
                    color: '#64748b',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    width: 32,
                    height: 32,
                    '&:hover': { color: themeColors.primary, borderColor: '#94a3b8', bgcolor: '#f8fafc' },
                  }}
                >
                  <OpenInNew sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            )}
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  )
}

export default memo(DocumentCard)
